import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/providers/AuthProvider';
import { useToast } from '@/hooks/use-toast';
import { 
  Bell, 
  BellOff, 
  Check, 
  X, 
  Settings, 
  Briefcase, 
  FileText, 
  RefreshCw, 
  User, 
  MessageSquare, 
  AlertCircle,
  Megaphone,
  Upload,
  Building2,
  UserCheck,
  Calendar,
  Clock,
  Target,
  Brain,
  CheckCircle,
  AlertTriangle,
  Info,
  Mail,
  Phone,
  MapPin,
  Zap,
  Star,
  BookOpen,
  Users,
  Wrench,
  Shield,
  Sparkles,
  CreditCard,
  ScrollText
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';

import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';
import { Link, useNavigate } from 'react-router-dom';
import { getNotificationNavigationPath } from '@/utils/notificationNavigation';

interface Notification {
  id: string;
  user_id: string;
  organization_uuid: string;
  type: string;
  title: string;
  message: string;
  data?: any;
  read_at: string | null;
  created_at: string;
}

export const NotificationBell: React.FC = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const { toast } = useToast();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  // Load notifications
  useEffect(() => {
    if (!user) return;
    loadNotifications();
  }, [user]);

  // Real-time subscription for new notifications
  useEffect(() => {
    if (!user) return;

    // Get user's organization for filtering
    const getUserOrganization = async () => {
      const { data: userOrg } = await supabase
        .from('user_organization')
        .select('organization_uuid')
        .eq('user_uuid', user.id)
        .single();
      
      return userOrg?.organization_uuid;
    };

    const setupRealtimeSubscription = async () => {
      const orgUuid = await getUserOrganization();
      console.log('Setting up real-time subscription for org:', orgUuid);
      
      const channel = supabase
        .channel(`notifications-realtime-${crypto.randomUUID()}`)
        .on(
          'postgres_changes',
          {
            event: 'INSERT',
            schema: 'public',
            table: 'notifications',
            filter: orgUuid ? `organization_uuid=eq.${orgUuid}` : undefined,
          },
          (payload) => {
            const newNotification = payload.new as Notification;
            console.log('Real-time INSERT received:', newNotification);
            
            // Only show notifications for current user
            if (newNotification.user_id === user.id) {
              console.log('Adding new notification for current user');
              setNotifications(prev => [newNotification, ...prev]);
              setUnreadCount(prev => prev + 1);
              
              // Show toast for new notification
              toast({
                title: newNotification.title,
                description: newNotification.message,
                duration: 5000,
              });
            }
          }
        )
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'notifications',
            filter: orgUuid ? `organization_uuid=eq.${orgUuid}` : undefined,
          },
          (payload) => {
            const updatedNotification = payload.new as Notification;
            console.log('Real-time UPDATE received:', updatedNotification);
            
            // Only update notifications for current user
            if (updatedNotification.user_id === user.id) {
              console.log('Updating notification for current user');
              setNotifications(prev => {
                const updated = prev.map(n => n.id === updatedNotification.id ? updatedNotification : n);
                // Calculate unread count from the updated state
                const unreadCount = updated.filter(n => !n.read_at).length;
                setUnreadCount(unreadCount);
                return updated;
              });
            }
          }
        )
        .subscribe();

      return channel;
    };

    let channel: any;
    let cancelled = false;
    setupRealtimeSubscription().then(ch => {
      if (cancelled)
        supabase.removeChannel(ch);
      else
        channel = ch;
    });

    return () => {
      cancelled = true;
      if (channel) {
        supabase.removeChannel(channel);
      }
    };
  }, [user, toast]); // Removed notifications from dependency array

  const loadNotifications = async () => {
    if (!user) return;

    try {
      setLoading(true);
      
      // Get user's organization first
      const { data: userOrg } = await supabase
        .from('user_organization')
        .select('organization_uuid')
        .eq('user_uuid', user.id)
        .single();
      
      if (!userOrg?.organization_uuid) {
        console.error('User organization not found');
        return;
      }
      
      // Get recent notifications for the current user and organization
      const { data: notificationsData, error: notificationsError } = await supabase
        .from('notifications')
        .select('*')
        .eq('user_id', user.id)
        .eq('organization_uuid', userOrg.organization_uuid)
        .order('created_at', { ascending: false })
        .limit(20);

      if (!notificationsError && notificationsData) {
        setNotifications(notificationsData);
        
        // Calculate unread count from the fetched notifications
        const unreadCount = notificationsData.filter(n => !n.read_at).length;
        setUnreadCount(unreadCount);
      }
    } catch (error) {
      console.error('Error loading notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationIds: string[]) => {
    try {
      const { error } = await supabase
        .from('notifications')
        .update({ read_at: new Date().toISOString() })
        .in('id', notificationIds);

      if (error) {
        throw error;
      }

      // Update local state and unread count
      setNotifications(prev => {
        const updated = prev.map(notification => 
          notificationIds.includes(notification.id) 
            ? { ...notification, read_at: new Date().toISOString() }
            : notification
        );
        
        // Calculate new unread count
        const unreadCount = updated.filter(n => !n.read_at).length;
        setUnreadCount(unreadCount);
        
        return updated;
      });
    } catch (error) {
      console.error('Error marking notifications as read:', error);
      toast({
        title: t('notifications.error', 'Error'),
        description: t('notifications.markReadError', 'Failed to mark notifications as read.'),
        variant: 'destructive',
      });
    }
  };

  const markAllAsRead = async () => {
    const unreadIds = notifications
      .filter(n => !n.read_at)
      .map(n => n.id);

    if (unreadIds.length > 0) {
      await markAsRead(unreadIds);
    }
  };

  const getNotificationIcon = (type: string, data?: any) => {
    switch (type) {
      // Job-related notifications
      case 'job_update':
      case 'info_changes':
        return <Briefcase className="h-5 w-5 text-blue-500" />;
      case 'new_application':
        return <FileText className="h-5 w-5 text-green-600" />;
      case 'application_status_change':
      case 'application_update':
        return <RefreshCw className="h-5 w-5 text-orange-600" />;
      case 'status_change':
        return <Target className="h-5 w-5 text-amber-600" />;
      case 'published_status':
        return <CheckCircle className="h-5 w-5 text-green-500" />;
      case 'deadline_approaching':
        return <Clock className="h-5 w-5 text-red-500" />;
      
      // Candidate-related notifications
      case 'candidate_update':
        return <User className="h-5 w-5 text-purple-600" />;
      case 'contact_info':
        return <Mail className="h-5 w-5 text-blue-500" />;
      case 'availability':
        return <Calendar className="h-5 w-5 text-teal-600" />;
      case 'skill_updates':
        return <Star className="h-5 w-5 text-yellow-600" />;
      case 'new_process':
        return <Users className="h-5 w-5 text-indigo-600" />;
      
      // Company-related notifications
      case 'company_update':
      case 'info_updates':
        return <Building2 className="h-5 w-5 text-slate-600" />;
      case 'contact_update':
        return <Phone className="h-5 w-5 text-blue-500" />;
      case 'new_job':
        return <Briefcase className="h-5 w-5 text-emerald-600" />;
      
      // Match-related notifications
      case 'new_match':
        return <UserCheck className="h-5 w-5 text-pink-600" />;
      case 'interview_scheduled':
        return <Calendar className="h-5 w-5 text-purple-500" />;
      
      // General notifications
      case 'comment_mention':
      case 'new_comment':
        return <MessageSquare className="h-5 w-5 text-cyan-600" />;
      case 'new_document':
        return <Upload className="h-5 w-5 text-emerald-600" />;
      case 'ai_analysis':
      case 'ai_processing_complete':
        return <Brain className="h-5 w-5 text-indigo-600" />;
      case 'system':
        return getSystemNotificationIcon(data?.category, data?.priority || 'medium');
      
      default:
        return <Bell className="h-5 w-5 text-gray-600" />;
    }
  };

  const getSystemNotificationIcon = (category: string | null, priority: string) => {
    // Determine base icon by category
    let IconComponent;
    let baseColor;
    
    switch (category) {
      case 'maintenance':
        IconComponent = Wrench;
        baseColor = 'orange';
        break;
      case 'security':
        IconComponent = Shield;
        baseColor = 'red';
        break;
      case 'feature':
        IconComponent = Sparkles;
        baseColor = 'purple';
        break;
      case 'billing':
        IconComponent = CreditCard;
        baseColor = 'green';
        break;
      case 'policy':
        IconComponent = ScrollText;
        baseColor = 'blue';
        break;
      case 'general':
      default:
        IconComponent = Megaphone;
        baseColor = 'slate';
        break;
    }
    
    // Determine color intensity by priority
    let colorIntensity;
    switch (priority) {
      case 'critical':
        colorIntensity = '700';
        break;
      case 'high':
        colorIntensity = '600';
        break;
      case 'medium':
        colorIntensity = '500';
        break;
      case 'low':
        colorIntensity = '400';
        break;
      default:
        colorIntensity = '500';
        break;
    }
    
    const colorClass = `text-${baseColor}-${colorIntensity}`;
    
    return <IconComponent className={`h-5 w-5 ${colorClass}`} />;
  };

  const formatTimeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));

    if (diffInMinutes < 1) return t('notifications.justNow', 'Just now');
    if (diffInMinutes < 60) return t('notifications.minutesAgo', '{{minutes}}m ago', { minutes: diffInMinutes });
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return t('notifications.hoursAgo', '{{hours}}h ago', { hours: diffInHours });
    
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return t('notifications.daysAgo', '{{days}}d ago', { days: diffInDays });
    
    return date.toLocaleDateString();
  };

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.read_at) {
      markAsRead([notification.id]);
    }

    // Use the navigation utility to determine where to go
    const navigationResult = getNotificationNavigationPath(notification);
    
    if (navigationResult.handled) {
      navigate(navigationResult.path);
    }

    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative"
          onClick={() => setOpen(!open)}
          data-tour="notification-bell"
        >
          <Bell className="h-5 w-5" />
          
          {unreadCount > 0 && (
            <Badge 
              variant="destructive" 
              className="absolute -top-1 -right-1 h-5 w-5 rounded-sm p-0 text-xs flex items-center justify-center"
            >
              {unreadCount > 99 ? '99+' : unreadCount}
            </Badge>
          )}
        </Button>
      </PopoverTrigger>
      
      <PopoverContent className="w-[28rem] p-0" align="end">
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="font-semibold">
            {t('notifications.title', 'Notifications')}
          </h3>
          <div className="flex items-center gap-2">
            {unreadCount > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={markAllAsRead}
                className="text-xs"
              >
                <Check className="h-3 w-3 mr-1" />
                {t('notifications.markAllRead', 'Mark all read')}
              </Button>
            )}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="sm">
                  <Settings className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem asChild>
                  <Link to="/settings?tab=notifications" onClick={() => setOpen(false)}>
                    {t('notifications.settings', 'Notification Settings')}
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={loadNotifications}>
                  {t('notifications.refresh', 'Refresh')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>

        <div className="max-h-[calc(100vh-200px)] min-h-[300px] overflow-y-auto scrollbar-hide">
          {loading ? (
            <div className="flex items-center justify-center p-8">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
            </div>
          ) : notifications.length === 0 ? (
            <div className="flex flex-col items-center justify-center p-8 text-center">
              <BellOff className="h-12 w-12 text-muted-foreground mb-2" />
              <p className="text-sm text-muted-foreground">
                {t('notifications.noNotifications', 'No notifications yet')}
              </p>
            </div>
          ) : (
            <div className="divide-y">
              {notifications.map((notification) => (
                <div
                  key={notification.id}
                  className={cn(
                    "p-4 cursor-pointer transition-colors",
                    !notification.read_at 
                      ? "bg-blue-50 dark:bg-blue-950/50 hover:bg-blue-100 dark:hover:bg-blue-950/70" 
                      : "hover:bg-muted/50"
                  )}
                  onClick={() => handleNotificationClick(notification)}
                >
                  <div className="flex items-start gap-3">
                    <div className="text-lg">
                      {getNotificationIcon(notification.type, notification.data)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <h4 className={cn(
                          "text-sm leading-tight",
                          !notification.read_at ? "font-bold" : "font-medium"
                        )}>
                          {notification.title}
                        </h4>
                        {!notification.read_at && (
                          <div className="w-2 h-2 bg-primary rounded-full flex-shrink-0 mt-1" />
                        )}
                      </div>
                      <p className={cn(
                        "text-sm mt-1 line-clamp-2",
                        !notification.read_at ? "text-foreground font-medium" : "text-muted-foreground"
                      )}>
                        {notification.message}
                      </p>
                      <p className="text-xs text-muted-foreground mt-2">
                        {formatTimeAgo(notification.created_at)}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {notifications.length > 0 && (
          <div className="p-4 border-t">
            <Button
              variant="ghost"
              size="sm"
              className="w-full"
              asChild
            >
              <Link to="/notifications">
                {t('notifications.viewAll', 'View all notifications')}
              </Link>
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}; 